import { useQuery } from '@tanstack/react-query';
import { Download } from 'lucide-react';
import { useMemo, useState } from 'react';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { listCodebases } from '@/lib/api';
import { buildBackendUrl } from '@/lib/auth-routing';
import { listWorkAreas } from '@/lib/work-areas-api';

type ExportScopeKind = 'codebase' | 'work_area';

function scopeParam(kind: ExportScopeKind): 'codebaseId' | 'workAreaId' {
  return kind === 'codebase' ? 'codebaseId' : 'workAreaId';
}

function buildExportHref(kind: ExportScopeKind, id: string): string | null {
  if (id.length === 0) return null;
  const params = new URLSearchParams({ [scopeParam(kind)]: id, format: 'zip' });
  return buildBackendUrl(`/api/admin/artifact-export?${params.toString()}`);
}

function sortByName<T extends { name: string }>(items: readonly T[]): T[] {
  const sorted = [...items];
  sorted.sort((a, b) => a.name.localeCompare(b.name));
  return sorted;
}

export function AdminExportButton(): React.ReactElement {
  const [scopeKind, setScopeKind] = useState<ExportScopeKind>('codebase');
  const [scopeId, setScopeId] = useState('');

  const codebasesQuery = useQuery({
    queryKey: ['admin-export-codebases'],
    queryFn: listCodebases,
    staleTime: 60_000,
    enabled: scopeKind === 'codebase',
  });
  const workAreasQuery = useQuery({
    queryKey: ['admin-export-work-areas'],
    queryFn: listWorkAreas,
    staleTime: 60_000,
    enabled: scopeKind === 'work_area',
  });

  const codebases = useMemo(() => sortByName(codebasesQuery.data ?? []), [codebasesQuery.data]);
  const workAreas = useMemo(
    () => sortByName(workAreasQuery.data?.workAreas ?? []),
    [workAreasQuery.data]
  );

  const href = buildExportHref(scopeKind, scopeId);
  const isLoading =
    (scopeKind === 'codebase' && codebasesQuery.isLoading) ||
    (scopeKind === 'work_area' && workAreasQuery.isLoading);
  const options =
    scopeKind === 'codebase'
      ? codebases.map(c => ({ id: c.id, label: c.name }))
      : workAreas.map(w => ({ id: w.id, label: w.name }));

  return (
    <Card>
      <CardHeader>
        <CardTitle>Artifact Export</CardTitle>
        <CardDescription>
          Streams a zip bundle of artifacts, snapshots, GitHub publications, applicability records,
          Work Area links, and the matching audit + activity log entries for the selected scope.
          Visibility filters still apply; private artifacts you do not own are excluded.
        </CardDescription>
      </CardHeader>
      <CardContent className="flex flex-col gap-3">
        <div className="inline-flex w-fit rounded-md border border-border p-1">
          <button
            type="button"
            className={`rounded px-3 py-1.5 text-sm font-medium transition-colors ${scopeKind === 'codebase' ? 'bg-[oklch(0.72_0.14_225/0.15)] text-accent-bright' : 'text-text-secondary hover:text-text-primary'}`}
            onClick={() => {
              setScopeKind('codebase');
              setScopeId('');
            }}
          >
            Codebase
          </button>
          <button
            type="button"
            className={`rounded px-3 py-1.5 text-sm font-medium transition-colors ${scopeKind === 'work_area' ? 'bg-[oklch(0.72_0.14_225/0.15)] text-accent-bright' : 'text-text-secondary hover:text-text-primary'}`}
            onClick={() => {
              setScopeKind('work_area');
              setScopeId('');
            }}
          >
            Work area
          </button>
        </div>
        <div className="flex flex-col gap-2 sm:flex-row">
          <select
            value={scopeId}
            onChange={event => {
              setScopeId(event.target.value);
            }}
            disabled={isLoading || options.length === 0}
            aria-label={scopeKind === 'codebase' ? 'Select codebase' : 'Select work area'}
            className="flex h-9 w-full rounded-md border border-border bg-surface px-3 text-sm shadow-sm transition-colors disabled:cursor-not-allowed disabled:opacity-50"
          >
            <option value="">
              {isLoading
                ? 'Loading…'
                : options.length === 0
                  ? scopeKind === 'codebase'
                    ? 'No codebases registered'
                    : 'No work areas registered'
                  : scopeKind === 'codebase'
                    ? 'Select a codebase…'
                    : 'Select a work area…'}
            </option>
            {options.map(opt => (
              <option key={opt.id} value={opt.id}>
                {opt.label}
              </option>
            ))}
          </select>
          {href ? (
            <Button asChild>
              <a href={href}>
                <Download className="mr-2 size-4" />
                Export
              </a>
            </Button>
          ) : (
            <Button type="button" disabled>
              <Download className="mr-2 size-4" />
              Export
            </Button>
          )}
        </div>
      </CardContent>
    </Card>
  );
}
